import * as React from 'react';
import client from '../api/contentful';
import { Entry } from 'contentful';

interface IProject {
  name: string;
  slug: string;
  archived?: boolean;
  ProjectContent: ProjectContent[];
}

export type ProjectEntry = Entry<IProject>;

export interface IProjectContent {
  title: string;
  description: string;
  type: 'Text' | '1 / 1' | '2 / 1' | '3 / 1' | '1 / 2';
  images: Image[];
}

export type ProjectContent = Entry<IProjectContent>;

interface IImage {
  title: string;
  description: string;
  file: {
    contentType: string;
    url: string;
    fileName: string;
    details: {
      image: {
        height: number;
        width: number;
      };
    };
  };
}

export type Image = Entry<IImage>;

export default class Project {
  static data: Map<string, ProjectEntry> = new Map();

  static promiseMap: Map<string, Promise<ProjectEntry>> = new Map();

  static getData(slug: string): Promise<ProjectEntry> {
    if (Project.promiseMap.has(slug)) {
      return Project.promiseMap.get(slug)!;
    }

    const promise = client
      .getEntries<IProject>({
        content_type: 'project',
        'fields.slug': slug,
      })
      .then((data) => {
        const project = data.items[0];

        Project.data.set(slug, project);

        return project;
      });

    Project.promiseMap.set(slug, promise);

    return promise;
  }
}

interface IRenderProps {
  data: ProjectEntry;
  slug: string;
}

interface IProps {
  slug: string;
  archived: boolean;
  render:
    | ((props: IRenderProps) => React.ReactNode)
    | React.ComponentClass<IRenderProps>;
  fallback?: React.SFC | React.ComponentClass;
  [key: string]: any;
}

interface IState {
  data: ProjectEntry | undefined;
}

function isComponentClass(render: any): render is React.ComponentClass {
  return undefined !== render.prototype.isReactComponent;
}

const blackList = new Set(['render', 'fallback', 'slug']);

export class ProjectRenderer extends React.PureComponent<IProps, IState> {
  state: IState = {
    data: undefined,
  };

  componentDidMount() {
    this.fetch(this.props.slug);
  }

  fetch(slug: string) {
    Project.getData(slug).then((data) => this.setState({ data }));
  }

  componentWillReceiveProps(nextProps: IProps) {
    if (this.props.slug !== nextProps.slug) {
      this.setState({ data: undefined });
      this.fetch(nextProps.slug);
    }
  }

  render() {
    const extraProps = Object.keys(this.props).reduce((acc, key) => {
      if (!blackList.has(key)) {
        acc[key] = this.props[key];
      }
      return acc;
    }, {});

    if (this.state.data) {
      if (isComponentClass(this.props.render)) {
        const Render = this.props.render;
        return (
          <Render
            data={this.state.data}
            slug={this.props.slug}
            {...extraProps}
          />
        );
      }

      return this.props.render({
        data: this.state.data,
        slug: this.props.slug,
        ...extraProps,
      });
    }

    if (this.props.fallback) {
      const Fallback = this.props.fallback;

      return <Fallback />;
    }

    return null;
  }
}



// WEBPACK FOOTER //
// ./src/models/Project.tsx